import React, { useRef, useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, Volume2, VolumeX } from "lucide-react";
import { UGC_VIDEOS } from "../mock";

const VideoCard = ({ src, poster }) => {
  const ref = useRef(null);
  const [muted, setMuted] = useState(true);

  // Only play while the card is in view (keeps playback smooth with many videos)
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          v.play().catch(() => {});
        } else {
          v.pause();
        }
      },
      { threshold: 0.35 }
    );
    io.observe(v);
    return () => io.disconnect();
  }, []);

  const toggle = () => {
    const v = ref.current;
    if (!v) return;
    v.muted = !v.muted;
    if (!v.muted) v.play().catch(() => {});
    setMuted(v.muted);
  };

  return (
    <div className="snap-start shrink-0 w-[240px] sm:w-[270px] relative rounded-lg overflow-hidden bg-[#1A1A1A] shadow-[0_20px_50px_-30px_rgba(26,26,26,0.5)]">
      <video
        ref={ref}
        className="w-full aspect-[9/16] object-cover"
        loop
        muted
        playsInline
        preload="metadata"
        poster={poster}
        src={src}
      />
      <button
        onClick={toggle}
        aria-label={muted ? "Unmute" : "Mute"}
        className="absolute bottom-3 right-3 h-9 w-9 rounded-full bg-black/45 backdrop-blur-sm text-white flex items-center justify-center hover:bg-black/70 transition-colors"
      >
        {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
      </button>
    </div>
  );
};

const VideoSlider = () => {
  const trackRef = useRef(null);

  const scroll = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const amount = Math.min(el.clientWidth * 0.8, 560);
    el.scrollBy({ left: dir * amount, behavior: "smooth" });
  };

  return (
    <section className="py-16 md:py-24 bg-[#F4EFE8]">
      <div className="max-w-[1280px] mx-auto px-5 md:px-8">
        <div className="flex items-end justify-between mb-10">
          <div>
            <h2 className="font-serif-display text-[18px] md:text-[26px] tracking-[0.3em] uppercase text-[#8a6d3b] font-medium">
              Real People, Real Glow
            </h2>
          </div>
          <div className="hidden md:flex gap-3">
            <button onClick={() => scroll(-1)} aria-label="Previous" className="btn-lux h-11 w-11 rounded-full">
              <ChevronLeft size={18} />
            </button>
            <button onClick={() => scroll(1)} aria-label="Next" className="btn-lux h-11 w-11 rounded-full">
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        <div
          ref={trackRef}
          className="flex gap-5 overflow-x-auto no-scrollbar snap-x snap-mandatory scroll-smooth pb-2"
        >
          {UGC_VIDEOS.map((src, i) => (
            <VideoCard key={i} src={src} poster={`/ugc-poster-${i}.jpg`} />
          ))}
        </div>

        <div className="flex md:hidden items-center justify-center gap-4 mt-6">
          <button onClick={() => scroll(-1)} aria-label="Previous" className="btn-lux h-11 w-11 rounded-full">
            <ChevronLeft size={18} />
          </button>
          <button onClick={() => scroll(1)} aria-label="Next" className="btn-lux h-11 w-11 rounded-full">
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default VideoSlider;
